import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { 
  ArrowLeft, 
  Clock, 
  Target, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  Trophy,
  BarChart3,
  Zap,
  Play,
  Pause,
  SkipForward,
  Download,
  Ship,
  Anchor,
  Iceberg
} from 'lucide-react';

// Types
interface Question {
  id: string;
  question: string;
  options?: string[];
  correct: string;
  time: number;
  category: string;
  subcategory: string;
  type?: 'mcq' | 'true_false' | 'fill_blank' | 'short_answer' | 'essay';
}

interface GameResult {
  won: boolean;
  amount?: number;
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  timeBonus: number;
}

// Animations
const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
`;

const slideIn = keyframes`
  0% { transform: translateX(100%); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
`;

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const timerWarning = keyframes`
  0%, 100% { background: #ff9800; }
  50% { background: #f44336; }
`;

// Ship Animations
const shipFloat = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-5px) rotate(1deg); }
  50% { transform: translateY(-2px) rotate(-1deg); }
  75% { transform: translateY(-7px) rotate(0.5deg); }
`;

const shipDock = keyframes`
  0% { transform: translateX(0px) translateY(0px) rotate(0deg); }
  50% { transform: translateX(-20px) translateY(-10px) rotate(-5deg); }
  100% { transform: translateX(-40px) translateY(-15px) rotate(-10deg); }
`;

const shipCapsize = keyframes`
  0% { transform: translateX(0px) translateY(0px) rotate(0deg); }
  25% { transform: translateX(10px) translateY(-5px) rotate(15deg); }
  50% { transform: translateX(20px) translateY(-10px) rotate(45deg); }
  75% { transform: translateX(30px) translateY(-15px) rotate(75deg); }
  100% { transform: translateX(40px) translateY(-20px) rotate(90deg); }
`;

const icebergFloat = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-3px) rotate(2deg); }
`;

const statueGlow = keyframes`
  0%, 100% { filter: drop-shadow(0 0 20px #ffd700); }
  50% { filter: drop-shadow(0 0 40px #ffd700) drop-shadow(0 0 60px #ffd700); }
`;

const confettiFall = keyframes`
  0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
  100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
`;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  color: white;
  position: relative;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const CompetitionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const InfoItem = styled.div`
  text-align: center;
`;

const InfoValue = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: #ffd700;
`;

const InfoLabel = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const TimerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px 25px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  border: 2px solid rgba(255, 255, 255, 0.2);
`;

const TimerIcon = styled.div<{ timeLeft: number }>`
  font-size: 1.5rem;
  color: ${props => {
    if (props.timeLeft <= 10) return '#f44336';
    if (props.timeLeft <= 20) return '#ff9800';
    return '#4caf50';
  }};
  animation: ${props => props.timeLeft <= 10 ? pulse : 'none'} 1s infinite;
`;

const Timer = styled.div<{ timeLeft: number }>`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => {
    if (props.timeLeft <= 10) return '#f44336';
    if (props.timeLeft <= 20) return '#ff9800';
    return '#4caf50';
  }};
  min-width: 80px;
  text-align: center;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  margin: 20px 0;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number; timeLeft: number }>`
  height: 100%;
  background: ${props => {
    if (props.timeLeft <= 10) return 'linear-gradient(90deg, #f44336, #e91e63)';
    if (props.timeLeft <= 20) return 'linear-gradient(90deg, #ff9800, #ff5722)';
    return 'linear-gradient(90deg, #4caf50, #8bc34a)';
  }};
  border-radius: 4px;
  transition: width 0.5s ease;
  width: ${props => props.progress}%;
  animation: ${props => props.timeLeft <= 10 ? timerWarning : 'none'} 1s infinite;
`;

const QuestionSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 30px;
  padding: 20px;
  animation: ${slideIn} 0.5s ease-out;
`;

const QuestionNumber = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50px;
  padding: 10px 25px;
  font-size: 1rem;
  font-weight: 600;
  color: #ffd700;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const QuestionText = styled.div`
  font-size: 1.8rem;
  font-weight: 600;
  text-align: center;
  line-height: 1.4;
  max-width: 800px;
  padding: 30px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: ${fadeIn} 0.8s ease-out;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  max-width: 900px;
  width: 100%;
  animation: ${fadeIn} 1s ease-out;
`;

const OptionButton = styled.button<{ isSelected: boolean; isCorrect?: boolean; showResult?: boolean }>`
  background: ${props => {
    if (props.showResult) {
      if (props.isCorrect) return 'rgba(76, 175, 80, 0.3)';
      if (props.isSelected && !props.isCorrect) return 'rgba(244, 67, 54, 0.3)';
      return 'rgba(255, 255, 255, 0.05)';
    }
    return props.isSelected ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)';
  }};
  border: 2px solid ${props => {
    if (props.showResult) {
      if (props.isCorrect) return '#4caf50';
      if (props.isSelected && !props.isCorrect) return '#f44336';
      return 'rgba(255, 255, 255, 0.1)';
    }
    return props.isSelected ? '#ffd700' : 'rgba(255, 255, 255, 0.2)';
  }};
  border-radius: 15px;
  padding: 20px;
  color: white;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: ${props => props.showResult ? 'default' : 'pointer'};
  transition: all 0.3s ease;
  text-align: left;
  position: relative;
  overflow: hidden;
  
  &:hover:not([disabled]) {
    transform: translateY(-3px);
    background: ${props => props.isSelected ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.1)'};
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    cursor: default;
  }
`;

const SelectedOptionButton = styled(OptionButton)`
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 215, 0, 0.2), transparent);
    animation: ${slideIn} 2s infinite;
  }
`;

const OptionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 10px;
`;

const OptionLetter = styled.div<{ isSelected: boolean; isCorrect?: boolean; showResult?: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  background: ${props => {
    if (props.showResult) {
      if (props.isCorrect) return '#4caf50';
      if (props.isSelected && !props.isCorrect) return '#f44336';
      return 'rgba(255, 255, 255, 0.2)';
    }
    return props.isSelected ? '#ffd700' : 'rgba(255, 255, 255, 0.2)';
  }};
  color: ${props => props.isSelected ? '#333' : 'white'};
  border: 2px solid ${props => {
    if (props.showResult) {
      if (props.isCorrect) return '#4caf50';
      if (props.isSelected && !props.isCorrect) return '#f44336';
      return 'rgba(255, 255, 255, 0.3)';
    }
    return props.isSelected ? '#ffd700' : 'rgba(255, 255, 255, 0.3)';
  }};
`;

const OptionText = styled.div`
  flex: 1;
  line-height: 1.4;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 30px;
  animation: ${fadeIn} 1.2s ease-out;
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' | 'success' | 'download' }>`
  background: ${props => {
    switch (props.variant) {
      case 'primary': return 'linear-gradient(45deg, #4caf50, #8bc34a)';
      case 'secondary': return 'rgba(255, 255, 255, 0.1)';
      case 'success': return 'linear-gradient(45deg, #2196f3, #03a9f4)';
      case 'download': return 'linear-gradient(45deg, #ff9800, #ff5722)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ResultOverlay = styled.div<{ show: boolean; won: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.won 
    ? 'linear-gradient(135deg, rgba(0, 100, 0, 0.95), rgba(0, 150, 0, 0.95))'
    : 'linear-gradient(135deg, rgba(100, 0, 0, 0.95), rgba(150, 0, 0, 0.95))'
  };
  backdrop-filter: blur(20px);
  display: ${props => props.show ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.8s ease-out;
`;

const ResultContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 40px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 30px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
`;

const ResultTitle = styled.h1<{ won: boolean }>`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 20px;
  color: ${props => props.won ? '#ffd700' : '#ff6b6b'};
  text-shadow: 0 0 30px ${props => props.won ? '#ffd700' : '#ff6b6b'};
`;

const ResultSubtitle = styled.p`
  font-size: 1.5rem;
  margin-bottom: 30px;
  color: rgba(255, 255, 255, 0.9);
`;

const ResultStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin: 30px 0;
`;

const StatItem = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 20px;
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ffd700;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const SceneContainer = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  margin: 30px 0;
  background: ${props => props.won 
    ? 'linear-gradient(180deg, #87ceeb 0%, #4682b4 50%, #2f4f4f 100%)'
    : 'linear-gradient(180deg, #87ceeb 0%, #4682b4 50%, #1a1a2e 100%)'
  };
  border-radius: 20px;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

const ShipElement = styled.div<{ won: boolean; animate: boolean }>`
  position: absolute;
  bottom: 50px;
  right: 100px;
  font-size: 4rem;
  color: #8b4513;
  animation: ${props => props.animate 
    ? (props.won ? shipDock : shipCapsize) 
    : shipFloat
  } 3s ease-in-out forwards;
  transform-origin: center bottom;
`;

const IcebergElement = styled.div<{ show: boolean }>`
  position: absolute;
  bottom: 30px;
  left: 150px;
  font-size: 3rem;
  color: #b0c4de;
  opacity: ${props => props.show ? 1 : 0};
  animation: ${props => props.show ? icebergFloat : 'none'} 2s ease-in-out infinite;
  transform-origin: center bottom;
`;

const HarborElement = styled.div<{ show: boolean }>`
  position: absolute;
  bottom: 20px;
  left: 100px;
  font-size: 2rem;
  color: #8b4513;
  opacity: ${props => props.show ? 1 : 0};
  animation: ${props => props.show ? fadeIn : 'none'} 2s ease-in-out;
`;

const StatueElement = styled.div<{ show: boolean }>`
  position: absolute;
  bottom: 120px;
  left: 120px;
  font-size: 3rem;
  color: #ffd700;
  opacity: ${props => props.show ? 1 : 0};
  animation: ${props => props.show ? statueGlow : 'none'} 2s ease-in-out infinite;
`;

const Confetti = styled.div<{ delay: number; left: number }>`
  position: absolute;
  top: -10px;
  left: ${props => props.left}px;
  width: 10px;
  height: 10px;
  background: ${['#ffd700', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4'][Math.floor(Math.random() * 5)]};
  border-radius: 50%;
  animation: ${confettiFall} 3s ease-in ${props => props.delay}s infinite;
`;

const LiveCompetition = ({ 
  question, 
  questionNumber, 
  totalQuestions, 
  timeLeft, 
  selectedAnswer, 
  onAnswerSelect, 
  onAnswerSubmit, 
  onBack,
  gameResult,
  onGameComplete
}) => {
  const [showResult, setShowResult] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showGameResult, setShowGameResult] = useState(false);
  const [captainLogsGenerated, setCaptainLogsGenerated] = useState(false);
  const confettiRef = useRef<HTMLDivElement>(null);

  const progress = ((question?.time || 30) - timeLeft) / (question?.time || 30) * 100;
  const optionLetters = ['A', 'B', 'C', 'D'];

  useEffect(() => {
    if (gameResult && !showGameResult) {
      setShowGameResult(true);
      generateCaptainLogs(gameResult);
      generateConfetti();
    }
  }, [gameResult]);

  const generateCaptainLogs = (result: GameResult) => {
    const timestamp = new Date().toISOString();
    const logContent = `
CAPTAIN'S LOG - COMPETITION COMPLETE
====================================
Date: ${new Date().toLocaleDateString()}
Time: ${new Date().toLocaleTimeString()}
Captain: ${localStorage.getItem('username') || 'Anonymous Navigator'}

MISSION STATUS: ${result.won ? 'SUCCESSFUL DOCKING AT NEW YORK HARBOR' : 'MISSION FAILED - SHIP CAPSIZED'}

COMPETITION RESULTS:
- Total Questions: ${result.totalQuestions}
- Correct Answers: ${result.correctAnswers}
- Accuracy Rate: ${((result.correctAnswers / result.totalQuestions) * 100).toFixed(1)}%
- Final Score: ${result.score}
- Time Bonus: ${result.timeBonus}
${result.won ? `- Prize Won: $${result.amount}` : '- No prize awarded'}

${result.won ? 
  'The ship successfully navigated through all challenges and docked safely at New York Harbor. The crew was greeted by the majestic Statue of Liberty, symbolizing victory and achievement. All systems operational, mission accomplished!' :
  'The ship encountered treacherous waters and collided with an iceberg. Despite the crew\'s best efforts, the vessel capsized. This serves as a reminder that even the most skilled navigators must remain vigilant. Mission failed, but lessons learned.'
}

END OF LOG
====================================
    `.trim();

    const blob = new Blob([logContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Captain_Log_${timestamp.split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    setCaptainLogsGenerated(true);
  };

  const generateConfetti = () => {
    if (confettiRef.current) {
      for (let i = 0; i < 50; i++) {
        const confetti = document.createElement('div');
        confetti.className = 'confetti';
        confetti.style.left = Math.random() * 100 + '%';
        confetti.style.animationDelay = Math.random() * 3 + 's';
        confetti.style.animationDuration = (Math.random() * 3 + 2) + 's';
        confetti.style.background = ['#ffd700', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4'][Math.floor(Math.random() * 5)];
        confettiRef.current.appendChild(confetti);
      }
    }
  };

  // Reset internal state when question changes
  useEffect(() => {
    setShowResult(false);
    setSubmitted(false);
    console.log('LiveCompetition: Reset internal state for new question', {
      questionId: question?.id,
      questionNumber,
      selectedAnswer,
      timeLeft
    });
  }, [question?.id, questionNumber, selectedAnswer, timeLeft]);

  const handleOptionSelect = (option: string) => {
    if (!submitted) {
      console.log('LiveCompetition: Option selected', { option, questionNumber, currentSelected: selectedAnswer });
      onAnswerSelect(option);
    } else {
      console.log('LiveCompetition: Cannot select option - already submitted');
    }
  };

  const handleSubmit = () => {
    if (selectedAnswer && !submitted) {
      setSubmitted(true);
      setShowResult(true);
      
      // Show result for 2 seconds before auto-advancing
      setTimeout(() => {
        onAnswerSubmit();
      }, 2000);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Safety check: ensure question exists and has required properties
  if (!question || !question.time) {
    console.error('LiveCompetition: Invalid question object:', question);
    return (
      <div style={{ textAlign: 'center', color: 'white', padding: '50px' }}>
        <h2>Question Not Available</h2>
        <p>The question data is missing or invalid.</p>
        <button
          onClick={onBack}
          style={{
            background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '25px',
            cursor: 'pointer',
            marginTop: '20px'
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  if (showGameResult && gameResult) {
    return (
      <ResultOverlay show={true} won={gameResult.won}>
        <ResultContent>
          <ResultTitle won={gameResult.won}>
            {gameResult.won ? '🏆 VICTORY! 🏆' : '💥 MISSION FAILED 💥'}
          </ResultTitle>
          
          <ResultSubtitle>
            {gameResult.won 
              ? 'Congratulations, Captain! You have successfully navigated to New York Harbor!'
              : 'The ship has capsized! Better luck next time, Captain.'
            }
          </ResultSubtitle>

          <SceneContainer won={gameResult.won}>
            <ShipElement 
              won={gameResult.won} 
              animate={true}
            >
              🚢
            </ShipElement>
            
            {gameResult.won ? (
              <>
                <HarborElement show={true}>🏗️</HarborElement>
                <StatueElement show={true}>🗽</StatueElement>
              </>
            ) : (
              <IcebergElement show={true}>🧊</IcebergElement>
            )}
            
            <div ref={confettiRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
          </SceneContainer>

          <ResultStats>
            <StatItem>
              <StatValue>{gameResult.score}</StatValue>
              <StatLabel>Final Score</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{gameResult.correctAnswers}/{gameResult.totalQuestions}</StatValue>
              <StatLabel>Correct Answers</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{((gameResult.correctAnswers / gameResult.totalQuestions) * 100).toFixed(1)}%</StatValue>
              <StatLabel>Accuracy</StatLabel>
            </StatItem>
            {gameResult.won && (
              <StatItem>
                <StatValue>${gameResult.amount}</StatValue>
                <StatLabel>Prize Won</StatLabel>
              </StatItem>
            )}
          </ResultStats>

          <ActionButtons>
            <ActionButton variant="download" onClick={() => generateCaptainLogs(gameResult)}>
              <Download size={20} />
              {captainLogsGenerated ? 'Logs Downloaded' : 'Download Captain Logs'}
            </ActionButton>
            
            <ActionButton variant="primary" onClick={() => onGameComplete && onGameComplete()}>
              <Trophy size={20} />
              {gameResult.won ? 'Claim Prize' : 'Try Again'}
            </ActionButton>
            
            <ActionButton variant="secondary" onClick={onBack}>
              <ArrowLeft size={20} />
              Back to Menu
            </ActionButton>
          </ActionButtons>
        </ResultContent>
      </ResultOverlay>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={onBack}>
            <ArrowLeft size={24} />
          </BackButton>
          <CompetitionInfo>
            <InfoItem>
              <InfoValue>{questionNumber}/{totalQuestions}</InfoValue>
              <InfoLabel>Question</InfoLabel>
            </InfoItem>
            <InfoItem>
              <InfoValue>{question.category}</InfoValue>
              <InfoLabel>Category</InfoLabel>
            </InfoItem>
            <InfoItem>
              <InfoValue>{question.subcategory}</InfoValue>
              <InfoLabel>Topic</InfoLabel>
            </InfoItem>
          </CompetitionInfo>
        </HeaderLeft>

        <TimerContainer>
          <TimerIcon timeLeft={timeLeft}>
            <Clock />
          </TimerIcon>
          <Timer timeLeft={timeLeft}>
            {formatTime(timeLeft)}
          </Timer>
        </TimerContainer>
      </Header>

      <ProgressBar>
        <ProgressFill progress={progress} timeLeft={timeLeft} />
      </ProgressBar>

      <QuestionSection>
        <QuestionNumber>
          Question {questionNumber} of {totalQuestions}
        </QuestionNumber>

        <QuestionText>
          {question.question}
        </QuestionText>

        <OptionsGrid>
          {question.options && question.options.length > 0 ? question.options.map((option, index) => {
            const isSelected = selectedAnswer === option;
            const ButtonComponent = isSelected && !showResult ? SelectedOptionButton : OptionButton;
            
            return (
              <ButtonComponent
                key={index}
                isSelected={isSelected}
                isCorrect={showResult ? option === question.correct : undefined}
                showResult={showResult}
                onClick={() => handleOptionSelect(option)}
                disabled={submitted}
              >
                <OptionLabel>
                  <OptionLetter
                    isSelected={isSelected}
                    isCorrect={showResult ? option === question.correct : undefined}
                    showResult={showResult}
                  >
                    {optionLetters[index]}
                  </OptionLetter>
                  <OptionText>{option}</OptionText>
                </OptionLabel>
              </ButtonComponent>
            );
          }) : (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
              {question.type === 'true_false' ? (
                <div>
                  <p>This is a True/False question. Please select your answer below.</p>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '10px' }}>
                    <button 
                      onClick={() => handleOptionSelect('True')}
                      disabled={submitted}
                      style={{ 
                        padding: '10px 20px', 
                        backgroundColor: selectedAnswer === 'True' ? '#4CAF50' : '#f0f0f0',
                        color: selectedAnswer === 'True' ? 'white' : 'black',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: submitted ? 'not-allowed' : 'pointer'
                      }}
                    >
                      True
                    </button>
                    <button 
                      onClick={() => handleOptionSelect('False')}
                      disabled={submitted}
                      style={{ 
                        padding: '10px 20px', 
                        backgroundColor: selectedAnswer === 'False' ? '#4CAF50' : '#f0f0f0',
                        color: selectedAnswer === 'False' ? 'white' : 'black',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: submitted ? 'not-allowed' : 'pointer'
                      }}
                    >
                      False
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p>This question type ({question.type}) doesn't support multiple choice options.</p>
                  <p>Please provide your answer in the text input below.</p>
                  <input
                    type="text"
                    value={selectedAnswer || ''}
                    onChange={(e) => handleOptionSelect(e.target.value)}
                    disabled={submitted}
                    placeholder="Enter your answer..."
                    style={{
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '5px',
                      width: '100%',
                      maxWidth: '300px',
                      marginTop: '10px'
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </OptionsGrid>

        <ActionButtons>
          {!submitted ? (
            <>
              <ActionButton
                variant="secondary"
                onClick={onBack}
                disabled={submitted}
              >
                <Pause size={20} />
                Pause
              </ActionButton>
              
                             <ActionButton
                 variant="primary"
                 onClick={handleSubmit}
                 disabled={!selectedAnswer || submitted}
               >
                 <CheckCircle size={20} />
                 Submit Answer {selectedAnswer ? `(${selectedAnswer})` : ''}
               </ActionButton>
            </>
          ) : (
            <ActionButton variant="success" disabled>
              <SkipForward size={20} />
              Next Question...
            </ActionButton>
          )}
        </ActionButtons>
      </QuestionSection>
    </Container>
  );
};

export default LiveCompetition;
